import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import { Experience } from '../../types';
import SkillChips from './SkillChips';
import ProjectCard from './ProjectCard';

const ExperienceCard: React.FC<{ experience: Experience; defaultExpanded?: boolean }> = ({ experience, defaultExpanded = false }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
                    <Box sx={{ flex: 1 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
                            <Typography variant="h5" sx={{ fontSize: { xs: '1.2rem', md: '1.4rem' }, fontWeight: 800 }}>{experience.company}</Typography>
                            <Chip label={experience.position} sx={{ mt: { xs: 1, sm: 0 }, fontWeight: 600, background: 'linear-gradient(135deg, #2563EB, #7C3AED)', color: '#fff', height: 30, fontSize: '0.8rem' }} />
                        </Stack>
                        <Stack direction="row" spacing={3} sx={{ mb: 1.5 }} flexWrap="wrap">
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <CalendarMonthRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">{experience.period}</Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <LocationOnRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">{experience.location}</Typography>
                            </Stack>
                        </Stack>
                        <Typography variant="body1" color="text.secondary">{experience.shortDescription}</Typography>
                    </Box>
                    <IconButton sx={{ ml: 2, width: 40, height: 40, borderRadius: '10px', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s', bgcolor: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' }}>
                        <ExpandMoreRoundedIcon />
                    </IconButton>
                </Box>

                <Collapse in={expanded} timeout={400}>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>{experience.fullDescription}</Typography>

                    <Typography variant="h6" sx={{ fontSize: '1rem', mb: 2, fontWeight: 700 }}>🏆 Ключевые достижения</Typography>
                    <Box sx={{ mb: 3 }}>
                        {experience.achievements.map((a, i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.5, pl: 1 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: 'linear-gradient(135deg, #2563EB, #7C3AED)', mt: 1, flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>{a}</Typography>
                            </Box>
                        ))}
                    </Box>

                    {experience.projects.length > 0 && (
                        <>
                            <Typography variant="h6" sx={{ fontSize: '1rem', mb: 2, fontWeight: 700 }}>📁 Проекты</Typography>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                {experience.projects.map(p => (
                                    <Grid item xs={12} md={6} key={p.name}><ProjectCard project={p} /></Grid>
                                ))}
                            </Grid>
                        </>
                    )}

                    <Typography variant="h6" sx={{ fontSize: '1rem', mb: 1.5, fontWeight: 700 }}>🛠 Технологии</Typography>
                    <SkillChips skills={experience.technologies} size="medium" />
                </Collapse>
            </CardContent>
        </Card>
    );
};

export default ExperienceCard;
